import moment from 'moment';
import { useEffect, useState } from 'react';

import {
  ComplexDateOutputValue,
  DateRangeValue,
  DiscreteValue,
  InternalState,
} from './types';

const DATE_FORMAT = 'YYYY-MM-DD';

const toDateString = (date: Date) => moment(date).format(DATE_FORMAT);

const toRangeDates = (dateFrom: string, dateTo: string): DateRangeValue => ({
  startDate: moment(dateFrom, DATE_FORMAT).toDate(),
  endDate: moment(dateTo, DATE_FORMAT).toDate(),
});

const outputValueToDateRange = (
  value: ComplexDateOutputValue | null,
): DateRangeValue | null => {
  if (!value) return null;

  if (value.type === 'single') {
    const date = moment(value.date, DATE_FORMAT).toDate();
    return { startDate: date, endDate: date };
  }

  return toRangeDates(value.dateFrom, value.dateTo);
};

const outputValueToInternalState = (
  value: ComplexDateOutputValue | null,
): InternalState | null => {
  if (value?.type !== 'discrete') return null;

  const { direction, granularity, amount, dateFrom, dateTo } = value;
  return {
    mode: 'discrete',
    discrete: { direction, granularity, amount },
    dateRange: toRangeDates(dateFrom, dateTo),
  };
};

const calculateDiscreteRange = ({
  direction,
  granularity,
  amount = 1,
}: DiscreteValue) => {
  const today = moment();

  if (direction === 'this') {
    return {
      startDate: today.clone().startOf(granularity).toDate(),
      endDate: today.clone().endOf(granularity).toDate(),
    };
  }

  if (direction === 'past') {
    return {
      startDate: today.clone().subtract(amount, granularity).toDate(),
      endDate: today.toDate(),
    };
  }

  return {
    startDate: today.toDate(),
    endDate: today.clone().add(amount, granularity).toDate(),
  };
};

export const useComplexDateValue = (
  value: ComplexDateOutputValue | null,
  onChange: (value: ComplexDateOutputValue | null) => void,
) => {
  const [internalState, setInternalState] = useState<InternalState | null>(
    () => outputValueToInternalState(value),
  );

  useEffect(() => {
    if (!value) setInternalState(null);
  }, [value]);

  const dateRange = internalState?.dateRange ?? outputValueToDateRange(value);

  const emitRange = (startDate: Date, endDate: Date) => {
    if (moment(startDate).isSame(endDate, 'day')) {
      onChange({ type: 'single', date: toDateString(startDate) });
      return;
    }

    onChange({
      type: 'range',
      dateFrom: toDateString(startDate),
      dateTo: toDateString(endDate),
    });
  };

  const handleDiscreteChange = (discrete: DiscreteValue) => {
    const dateRange = calculateDiscreteRange(discrete);
    setInternalState({ mode: 'discrete', discrete, dateRange });
    onChange({
      type: 'discrete',
      ...discrete,
      dateFrom: toDateString(dateRange.startDate),
      dateTo: toDateString(dateRange.endDate),
    });
  };

  const handlePickerChange = (dateRange: DateRangeValue) => {
    setInternalState({ mode: 'picker', dateRange });

    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return;
    emitRange(startDate, endDate);
  };

  const handleClear = () => {
    setInternalState(null);
    onChange(null);
  };

  return {
    internalState,
    dateRange,
    handleDiscreteChange,
    handlePickerChange,
    handleClear,
  };
};
